import DOMPurify from 'dompurify';
import { ArticleData } from './types';

// Allowlist of trusted iframe sources for embedded content
const ALLOWED_IFRAME_HOSTS = [
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
  'vimeo.com',
  'www.dailymotion.com',
  'player.dailymotion.com',
  'open.spotify.com',
  'w.soundcloud.com',
  'www.google.com', // Google Maps, Forms
  'docs.google.com',
  'drive.google.com',
  'calendar.google.com',
  'codepen.io',
  'codesandbox.io',
];

function isAllowedIframeSource(src: string | null): boolean {
  if (!src) return false;
  try {
    const url = new URL(src);
    // Only allow HTTPS
    if (url.protocol !== 'https:') return false;
    return ALLOWED_IFRAME_HOSTS.includes(url.hostname);
  } catch {
    return false;
  }
}

// Configure DOMPurify with iframe source validation and image lazy loading
function getSanitizedContent(content: string): string {
  // Add hook to validate iframe sources and add lazy loading to images
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'iframe') {
      const element = node as Element;
      const src = element.getAttribute?.('src');
      if (!isAllowedIframeSource(src)) {
        element.parentNode?.removeChild(element);
        console.warn('Removed iframe with untrusted source:', src);
      }
    }

    // Add lazy loading to all images for performance
    if (data.tagName === 'img') {
      const element = node as Element;
      element.setAttribute('loading', 'lazy');
    }
  });

  const sanitized = DOMPurify.sanitize(content || '', {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'loading']
  });

  // Remove hook after sanitization to avoid memory leaks
  DOMPurify.removeHook('uponSanitizeElement');

  return sanitized;
}

interface ArticleContentProps {
  article: ArticleData;
}

export function ArticleContent({ article }: ArticleContentProps) {
  return (
    <div 
      className="prose prose-sm max-w-none dark:prose-invert
        prose-headings:font-bold prose-headings:text-foreground
        prose-h1:text-2xl prose-h1:mt-6 prose-h1:mb-3
        prose-h2:text-xl prose-h2:mt-5 prose-h2:mb-2
        prose-h3:text-lg prose-h3:mt-4 prose-h3:mb-2
        prose-p:text-foreground prose-p:leading-relaxed prose-p:mb-3
        prose-a:text-primary prose-a:no-underline hover:prose-a:underline
        prose-strong:text-foreground prose-strong:font-semibold
        prose-ul:my-3 prose-ol:my-3
        prose-li:text-foreground prose-li:my-1
        prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground
        prose-code:text-primary prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
        prose-pre:bg-muted prose-pre:border
        prose-img:rounded-lg prose-img:shadow-md"
      dangerouslySetInnerHTML={{ 
        __html: getSanitizedContent(article.content)
      }}
    />
  );
}
